"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDateLong } from "@/lib/format/date";
import {
  Package,
  Shirt,
  Clock,
  AlertTriangle,
  ChevronRight,
  Play,
  CheckCircle,
  Truck,
} from "lucide-react";
import { differenceInHours } from "date-fns";

interface ProductionOrder {
  id: string;
  publicId: string;
  orderType: string;
  status: string;
  itemCount: number;
  promisedDeliveryAt?: string | null;
  customer: {
    publicId: string;
    name: string;
  };
}

interface ProductionKanbanProps {
  orders: ProductionOrder[];
  onStartProduction?: (orderId: string) => void;
  onMarkQc?: (orderId: string) => void;
  onMarkReady?: (orderId: string) => void;
  onViewOrder: (orderId: string) => void;
  loading?: boolean;
}

const columns = [
  { key: "PAID", label: "Ready to Start", icon: Play },
  { key: "IN_PRODUCTION", label: "In Production", icon: Shirt },
  { key: "QC", label: "Quality Check", icon: CheckCircle },
  { key: "READY_FOR_DELIVERY", label: "Ready for Delivery", icon: Truck },
];

function OrderCard({
  order,
  onAction,
  actionLabel,
  onView,
}: {
  order: ProductionOrder;
  onAction?: () => void;
  actionLabel?: string;
  onView: () => void;
}) {
  const isUrgent =
    order.promisedDeliveryAt &&
    differenceInHours(new Date(order.promisedDeliveryAt), new Date()) < 24;

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={onView}
    >
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-sm">{order.publicId}</span>
          </div>
          <Badge variant="outline" className="text-xs">
            {order.orderType === "ALTERATION" ? "ALT" : "STI"}
          </Badge>
        </div>

        <div className="text-sm text-muted-foreground">
          {order.customer.name}
        </div>

        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1">
            <Shirt className="h-3 w-3" />
            <span>{order.itemCount} items</span>
          </div>

          {order.promisedDeliveryAt && (
            <div
              className={`flex items-center gap-1 ${
                isUrgent ? "text-amber-600" : "text-muted-foreground"
              }`}
            >
              {isUrgent && <AlertTriangle className="h-3 w-3" />}
              <Clock className="h-3 w-3" />
              <span>{formatDateLong(order.promisedDeliveryAt)}</span>
            </div>
          )}
        </div>

        {onAction && actionLabel && (
          <Button
            size="sm"
            className="w-full mt-2"
            onClick={(e) => {
              e.stopPropagation();
              onAction();
            }}
          >
            {actionLabel}
            <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export function ProductionKanban({
  orders,
  onStartProduction,
  onMarkQc,
  onMarkReady,
  onViewOrder,
  loading = false,
}: ProductionKanbanProps) {
  const getOrdersForColumn = (status: string) =>
    orders.filter((o) => o.status === status);

  const getActionForStatus = (
    status: string,
    orderId: string
  ): { label: string; handler: () => void } | null => {
    switch (status) {
      case "PAID":
        return onStartProduction
          ? { label: "Start Production", handler: () => onStartProduction(orderId) }
          : null;
      case "IN_PRODUCTION":
        return onMarkQc
          ? { label: "Mark for QC", handler: () => onMarkQc(orderId) }
          : null;
      case "QC":
        return onMarkReady
          ? { label: "Mark Ready", handler: () => onMarkReady(orderId) }
          : null;
      default:
        return null;
    }
  };

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Package className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="font-semibold text-lg mb-2">No Paid Orders Yet</h3>
        <p className="text-muted-foreground max-w-md">
          Orders will appear here once customers approve quotes and complete
          payment. Check the Quotes section for pending approvals.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {columns.map((column) => {
        const columnOrders = getOrdersForColumn(column.key);
        const Icon = column.icon;

        return (
          <div key={column.key} className="space-y-3">
            <div className="flex items-center gap-2 px-2">
              <Icon className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-medium text-sm">{column.label}</h3>
              <Badge variant="secondary" className="ml-auto">
                {columnOrders.length}
              </Badge>
            </div>

            <ScrollArea className="h-[calc(100vh-300px)]">
              <div className="space-y-3 pr-2">
                {columnOrders.map((order) => {
                  const action = getActionForStatus(column.key, order.id);
                  return (
                    <OrderCard
                      key={order.id}
                      order={order}
                      onAction={action?.handler}
                      actionLabel={action?.label}
                      onView={() => onViewOrder(order.id)}
                    />
                  );
                })}

                {columnOrders.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No orders
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        );
      })}
    </div>
  );
}
